'use client';
import { useState, useMemo, useEffect } from 'react';

import Link from 'next/link';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';

import { SIDE_MENU_ITEMS } from '@constants/constants';
import {
  PhoneHeader,
  PhoneIcon,
  SeturLogoSpecial,
  SeturLogoMobile,
} from '@assets/icons';
import CustomIcon from '@components/utils/CustomIcon';
import { addContactUsClickEventsToGTM } from '@utils/gtm/contact-us-gtm';
import { useBasketId } from '@hooks/cache';
import { useScroll } from '@hooks/hooks';
import { useDevice } from '@contexts/device';
import { useProfileName } from '@hooks/useProfileName';
import { usePointHistoryv2 } from '@hooks/usePointHistoryv2';
import { useSkipEffectOnMount } from '@hooks/lifecycle';
import { useInsiderBannerContext } from '@contexts/insiderBanners';
import useToken from '@utils/useToken';
import { HeaderViewModel } from '@api/v3';

import HeaderMenuItem from '../components/HeaderMenuItem';
import {
  HeaderWrapper,
  HeaderContentWrapper,
  HeaderLogo,
  HeaderMenu,
  ContactBoxLabel,
  ContactBoxNumber,
  ContactBoxSubLabel,
  HeaderBgWrapper,
  HeaderLogoWrapper,
  PopoverWrapper,
  ContactWrapper,
  LineContainer,
  Line,
  HeaderContactBoxContent,
  HeaderContactBox,
  HeaderWrapperMobile,
  HeaderContentWrapperMobile,
  VerticalLine,
  Wrapper,
} from './HeaderStyles';

const MobileHeaderContent = dynamic(() => import('./MobileHeaderContent'));
const DesktopHeaderContent = dynamic(() => import('./DesktopHeaderContent'));
const Popover = dynamic(() => import('../components/Popover'));

const MobileHeaderWithGTM = dynamic(() =>
  import('../MobileHeader/MobileHeader').then(
    (module) => module.MobileHeaderWithGTM
  )
);

export interface HeaderProps {
  hideBoxShadow?: boolean;
  isHomepage?: boolean;
  data?: HeaderViewModel | undefined;
  setIsLoading: (val: boolean) => void;
  isLoading?: boolean;
}

const Header = ({
  hideBoxShadow = false,
  isHomepage,
  data,
  setIsLoading,
  isLoading,
}: HeaderProps) => {
  const [visibleProfileMenu, setVisibleProfileMenu] = useState(false);
  const [showMobileHeader, setShowMobileHeader] = useState<boolean>(false);

  const { isDesktop, isMobile, isTablet } = useDevice();
  const scroll = useScroll();
  const basketId = useBasketId();
  const [mobileHeaderKey, setMobileHeaderKey] = useState(0);
  const { puanExtraBannerStatus } = useInsiderBannerContext();
  const pathname = usePathname();

  const token = useToken();
  const {
    profileName: { profileName },
  } = useProfileName();

  const { userSeturPoints: seturPoints } = usePointHistoryv2(!!token);

  useSkipEffectOnMount(() => {
    if (isLoading) {
      setShowMobileHeader(false);
      setMobileHeaderKey((val) => val + 1); // Reset mobile header when the user navigate from one page to another
    }
  }, [isLoading]);

  // ===================================================
  // Toggle visibility of the mobile header and profile menu
  // ===================================================
  const toggleMobileHeader = () => setShowMobileHeader((val) => !val);

  const toggleProfileMenu = () => setVisibleProfileMenu((state) => !state);

  // ==========================================
  // Hide and show drop-down base on scroll event
  // ==========================================
  useEffect(() => {
    if (scroll.y === 0 && isDesktop && isHomepage) {
      setVisibleProfileMenu(false);
    } else if (!isHomepage && scroll.y > 100) {
      setVisibleProfileMenu(false);
    }
  }, [scroll.y]);
  const basketHref = useMemo(
    () => (basketId ? `seyahat-planlama?bid=${basketId}` : 'seyahat-planlama'),
    [basketId]
  );
  const displayPoints = Boolean(seturPoints && profileName);
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (pathname === '/') {
      e.preventDefault(); // Aynı sayfadaysa yeniden yönlendirme yapma
    }
  };
  return (
    <>
      <header>
        {isDesktop ? (
          <HeaderWrapper
            hideBoxShadow={hideBoxShadow}
            popoverVisible={visibleProfileMenu}
          >
            {isMobile || isTablet ? (
              <MobileHeaderWithGTM
                profileName={profileName}
                visible={showMobileHeader}
                setIsLoading={setIsLoading}
                setShowMobileHeader={setShowMobileHeader}
                toggleVisibility={toggleMobileHeader}
                items={data?.menu || []}
                phone={data?.phone || []}
                key={mobileHeaderKey}
              />
            ) : null}
            <HeaderBgWrapper>
              <Wrapper>
                <CustomIcon icon={PhoneHeader} fontSize={14} />
                <Link
                  href={data?.phone?.[0]?.href?.split?.('/')?.[1] || ''}
                  onClick={() => {
                    addContactUsClickEventsToGTM('Top Menu', 'Call Center');
                  }}
                  prefetch={false}
                  title='Setur Telefon'
                >
                  <ContactBoxNumber>{data?.phone?.[0]?.name}</ContactBoxNumber>
                </Link>
              </Wrapper>
              <Wrapper>
                <Link
                  href={data?.agents?.[0]?.href || ''}
                  onClick={() =>
                    data?.agents?.[0]?.href === pathname
                      ? setIsLoading(false)
                      : setIsLoading(true)
                  }
                  prefetch={false}
                  title='Yetkili Seyahat Acenteler'
                >
                  <ContactWrapper>
                    <ContactBoxLabel>Acenteler</ContactBoxLabel>
                  </ContactWrapper>
                </Link>
              </Wrapper>
              {isDesktop ? (
                <DesktopHeaderContent
                  setIsLoading={setIsLoading}
                  displayPoints={displayPoints}
                  profileName={profileName}
                  basketHref={basketHref}
                  toggleProfileMenu={toggleProfileMenu}
                  seturPoints={seturPoints}
                />
              ) : null}
            </HeaderBgWrapper>
            <HeaderLogoWrapper header={true}>
              <Link
                href='/'
                onClick={handleClick}
                title='Setur'
                prefetch={false}
              >
                <CustomIcon icon={SeturLogoSpecial} width={212} height={36} />
              </Link>
              <VerticalLine />
              <ContactBoxSubLabel>
                Setur Servis Turistik A.Ş. <br />
                Belge No: 728
              </ContactBoxSubLabel>
            </HeaderLogoWrapper>
            <LineContainer>
              <Line />
            </LineContainer>
            <HeaderContentWrapper profileName={Boolean(profileName)}>
              {isMobile || isTablet ? (
                <MobileHeaderContent
                  displayPoints={displayPoints}
                  profileName={profileName}
                  basketHref={basketHref}
                  toggleProfileMenu={toggleProfileMenu}
                  toggleMobileHeader={toggleMobileHeader}
                  seturPoints={seturPoints}
                />
              ) : null}
              <HeaderMenu>
                {data?.menu?.map((item) => (
                  <HeaderMenuItem
                    key={item?.name}
                    label={item?.name}
                    href={item?.href === '#' ? '/#' : item?.href}
                    items={item?.categories}
                    campaigns={item?.campaigns}
                    setIsLoading={setIsLoading}
                    isLoading={isLoading}
                  />
                ))}
              </HeaderMenu>
            </HeaderContentWrapper>

            {visibleProfileMenu ? (
              <PopoverWrapper>
                <Popover
                  profile={pathname?.includes('profil') ? true : false}
                  setIsLoading={setIsLoading}
                  onClose={() => setVisibleProfileMenu(false)}
                  items={SIDE_MENU_ITEMS}
                />
              </PopoverWrapper>
            ) : null}
          </HeaderWrapper>
        ) : (
          <HeaderWrapperMobile
            hideBoxShadow={hideBoxShadow}
            popoverVisible={visibleProfileMenu}
          >
            {isMobile || isTablet ? (
              <MobileHeaderWithGTM
                profileName={profileName}
                visible={showMobileHeader}
                setIsLoading={setIsLoading}
                setShowMobileHeader={setShowMobileHeader}
                toggleVisibility={toggleMobileHeader}
                items={data?.menu || []}
                phone={data?.phone || []}
                key={mobileHeaderKey}
              />
            ) : null}

            <HeaderLogoWrapper header={true}>
              <Link href='/' title='Setur' prefetch={false}>
                <HeaderLogo>
                  <CustomIcon icon={SeturLogoMobile} width={78} height={54} />
                </HeaderLogo>
              </Link>
            </HeaderLogoWrapper>
            <HeaderContactBox>
              <HeaderContactBoxContent>
                <CustomIcon icon={PhoneIcon} fontSize={20} />
                <Link
                  href={data?.phone?.[0]?.href?.split?.('/')?.[1] || ''}
                  onClick={() => {
                    addContactUsClickEventsToGTM('Top Menu', 'Call Center');
                  }}
                  prefetch={false}
                >
                  <ContactBoxNumber>{data?.phone?.[0]?.name}</ContactBoxNumber>
                </Link>
                <Link href={data?.agents?.[0]?.href || ''} prefetch={false}>
                  <ContactBoxLabel>{data?.agents?.[0]?.name}</ContactBoxLabel>
                </Link>

                <ContactBoxSubLabel>
                  Setur Servis Turistik A.Ş. <br />
                  Belge No: 728
                </ContactBoxSubLabel>
              </HeaderContactBoxContent>
            </HeaderContactBox>
            <HeaderContentWrapperMobile>
              {isMobile || isTablet ? (
                <MobileHeaderContent
                  setIsLoading={setIsLoading}
                  displayPoints={displayPoints}
                  profileName={profileName}
                  basketHref={basketHref}
                  toggleProfileMenu={toggleProfileMenu}
                  toggleMobileHeader={toggleMobileHeader}
                  seturPoints={seturPoints}
                />
              ) : null}
              <HeaderMenu>
                {data?.menu?.map((item) => (
                  <HeaderMenuItem
                    key={item?.name}
                    label={item?.name}
                    href={item?.href === '#' ? '/#' : item?.href}
                    items={item?.categories}
                    campaigns={item?.campaigns}
                    setIsLoading={setIsLoading}
                    isLoading={isLoading}
                  />
                ))}
              </HeaderMenu>
            </HeaderContentWrapperMobile>
            {isDesktop ? (
              <DesktopHeaderContent
                displayPoints={displayPoints}
                profileName={profileName}
                basketHref={basketHref}
                toggleProfileMenu={toggleProfileMenu}
                seturPoints={seturPoints}
              />
            ) : null}
            {visibleProfileMenu ? (
              <PopoverWrapper>
                <Popover
                  profile={pathname?.includes('profil') ? true : false}
                  setIsLoading={setIsLoading}
                  onClose={() => setVisibleProfileMenu(false)}
                  items={SIDE_MENU_ITEMS}
                />
              </PopoverWrapper>
            ) : null}
          </HeaderWrapperMobile>
        )}
      </header>

      {puanExtraBannerStatus ? <div id='puan_ekstra_insider' /> : null}
    </>
  );
};

export default Header;
